import { lookupAccount } from './helpers';
import { filteredMatchingTransactions } from './potentialMitoMatchFilter';
import { vendorTooltipIconSize } from './VendorDetailsTooltip';
import { DataEntryRowProps } from './types';

const SUGGESTED_LEDGER_HEIGHT = 30;
const SUGGESTED_LEDGER_GAP = 8;

// This is inherited from `TextBlock`
const LINE_HEIGHT = 16;

export const BORDER_WIDTH = 1;
export const DATA_ENTRY_ROW_PADDING_HEIGHT = 4;
export const NOTE_ROW_GAP = 8;

export const getDataEntryRowHeight = ({
  dataEntry,
  showCategorizationSuggestions,
  matchingAmountTransactions = [],
  filterMito,
  isMoneyInTransitOutstanding,
  showLedgerAccountName,
}: DataEntryRowProps) => {
  const dataEntryAccount = lookupAccount(dataEntry);
  const { journalEntry, ledgerAccount } = dataEntry;
  const matches = filteredMatchingTransactions({
    matchingAmountTransactions,
    dataEntryAccount,
    journalEntry,
  });
  if (filterMito && isMoneyInTransitOutstanding && matches.length === 0) {
    return 0;
  } // Skip height calculation when mito is filtered and this row has no potential matches

  const { clientCatResponse, clientComments, internalNotes, categorization, vendorName } =
    journalEntry;

  // Calculate how many lines the transaction name will take
  // The TransactionName column has flex: 1 and min-width: 120px
  // Other columns take up: DateColumn (70px), AmountColumn x2 (160px), TooltipColumn (40px)
  // Plus optional columns and padding/margins
  const CHARS_PER_LINE = 50;
  let transactionNameText = vendorName || '';
  if (showLedgerAccountName && ledgerAccount) {
    transactionNameText += ` — ${ledgerAccount.name}`;
  }
  const transactionNameLines = Math.max(1, Math.ceil(transactionNameText.length / CHARS_PER_LINE));

  // Base height now accounts for multiple lines of transaction name
  // Adding padding only once (not multiplied by lines)
  const baseHeight =
    LINE_HEIGHT * transactionNameLines + BORDER_WIDTH + 2 * DATA_ENTRY_ROW_PADDING_HEIGHT;

  let categorizationSuggestionHeight = 0;
  const additionalSuggestedLedgers =
    categorization && categorization.suggestedLedgers && categorization.suggestedLedgers.length - 1;
  if (
    showCategorizationSuggestions &&
    additionalSuggestedLedgers &&
    additionalSuggestedLedgers > 1
  ) {
    categorizationSuggestionHeight =
      (SUGGESTED_LEDGER_GAP + SUGGESTED_LEDGER_HEIGHT) * additionalSuggestedLedgers;
  }

  let noteGroups = 0;
  let totalComments = 0;
  if (clientCatResponse) {
    noteGroups++;
    totalComments++;
  }
  if (clientComments.length > 0) {
    noteGroups++;
    totalComments += clientComments.length;
  }
  if (internalNotes.length > 0) {
    noteGroups++;
    totalComments += internalNotes.length;
  }
  if (noteGroups <= 1 && totalComments <= 1) {
    return Math.max(baseHeight, vendorTooltipIconSize);
  }
  // Notes in addition to the base 1 line height from the vendor name
  const additionalNoteBodyHeights = (totalComments - 1) * LINE_HEIGHT;
  // Gaps are only included in between adjacent rows, so it's one less than the number of rows
  const spaceBetweenNoteGroups = (noteGroups - 1) * NOTE_ROW_GAP;
  const calculatedHeight =
    baseHeight +
    spaceBetweenNoteGroups +
    additionalNoteBodyHeights +
    categorizationSuggestionHeight;
  return Math.max(calculatedHeight, vendorTooltipIconSize);
};
